// Firestore Service for AAR Guest Management System
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  documentId,
  limit as fsLimit
} from "firebase/firestore";
// Writes go through the view-as guard (read-only while a sys_admin views the
// app as another member).
import { setDoc, updateDoc, deleteDoc, runTransaction, httpsCallable } from "./guardedWrites";
import { db } from "./config";

// ==================== CHECK-IN/CHECK-OUT ====================

/**
 * Error thrown when a check-in is rejected because the person already has an
 * active (open) check-in. Callers can inspect `code === 'ALREADY_CHECKED_IN'`
 * and `existingCheckInId` to redirect to the existing record (e.g. check-out).
 */
export class AlreadyCheckedInError extends Error {
  constructor(message, existingCheckInId = null) {
    super(message);
    this.name = 'AlreadyCheckedInError';
    this.code = 'ALREADY_CHECKED_IN';
    this.existingCheckInId = existingCheckInId;
  }
}

/**
 * Local (device-timezone) date key, e.g. "2026-07-20". Used to scope
 * deterministic per-day check-in document IDs so a person can only be checked
 * in once per service per calendar day.
 * @param {Date} [date] - Defaults to now
 * @returns {string} YYYY-MM-DD
 */
const getLocalDateKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Find an existing OPEN (status === 'checked_in') check-in for a guest, if any.
 * Fresh server read — used as the real-time validation that the UI's cached
 * "today's check-ins" state cannot provide (e.g. checked in on another device).
 * @param {string} guestId
 * @returns {Promise<Object|null>} The open check-in record, or null
 */
export const findOpenCheckInForGuest = async (guestId) => {
  if (!guestId || guestId === 'new') return null;
  const checkInsRef = collection(db, "checkIns");
  const q = query(
    checkInsRef,
    where("guestId", "==", guestId),
    where("status", "==", "checked_in")
  );
  const snap = await getDocs(q);
  const open = snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(c => !c.guestDeleted);
  return open[0] || null;
};

/**
 * Get check-ins for a specific date
 * @param {Date} date
 * @returns {Promise<Array>} Array of check-in records
 */
export const fetchCheckInsForDate = async (date) => {
  try {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const checkInsRef = collection(db, "checkIns");
    const q = query(
      checkInsRef,
      where("checkInTime", ">=", Timestamp.fromDate(startOfDay)),
      where("checkInTime", "<=", Timestamp.fromDate(endOfDay)),
      orderBy("checkInTime", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      // Exclude check-ins for deleted guests
      .filter(checkIn => !checkIn.guestDeleted);
  } catch (error) {
    console.error("Error fetching check-ins:", error);
    throw error;
  }
};

/**
 * Subscribe to check-ins for today
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export const subscribeToTodayCheckIns = (callback, selectedDate = null) => {
  const targetDate = selectedDate || new Date();
  const startOfDay = new Date(targetDate);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(targetDate);
  endOfDay.setHours(23, 59, 59, 999);

  const checkInsRef = collection(db, "checkIns");
  const q = query(
    checkInsRef,
    where("checkInTime", ">=", Timestamp.fromDate(startOfDay)),
    where("checkInTime", "<=", Timestamp.fromDate(endOfDay)),
    orderBy("checkInTime", "desc")
  );

  return onSnapshot(q, (querySnapshot) => {
    const checkIns = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      // Exclude notes (serviceType === 'note') from check-ins list
      // Notes are stored in checkIns collection but shouldn't count as visitors
      .filter(checkIn => checkIn.serviceType !== 'note')
      // Exclude check-ins for deleted guests
      .filter(checkIn => !checkIn.guestDeleted);
    callback(checkIns);
  });
};

/**
 * Fetch today's check-ins
 * @returns {Promise<Array>} Array of today's check-in records
 */
export const fetchTodayCheckIns = async () => {
  try {
    const today = new Date();
    const checkIns = await fetchCheckInsForDate(today);
    // Exclude notes (serviceType === 'note') and deleted guests from check-ins list
    return checkIns
      .filter(checkIn => checkIn.serviceType !== 'note')
      .filter(checkIn => !checkIn.guestDeleted);
  } catch (error) {
    console.error("Error fetching today's check-ins:", error);
    throw error;
  }
};

/**
 * Check in a guest
 * @param {Object} checkInData
 * @returns {Promise<string>} Check-in ID
 */
export const checkInGuest = async (checkInData) => {
  try {
    let guestId = checkInData.guestId;
    let ccbId = null;
    let ccbImageUploaded = false;
    // Tracks whether the new individual was successfully added to the AAR
    // CCB group during profile creation. When this is false we persist
    // `needsGroupAdd` on the participant doc and rely on the scheduled
    // reconciler (and a delayed retry below) to add them later.
    let addedToCCBGroup = true;

    // Real-time double check-in guard (fail fast for existing guests):
    // a fresh server read catches the case where the guest was already
    // checked in on another device since this device loaded its list.
    // New guests (guestId === 'new') can't have a prior open check-in.
    if (checkInData.guestId && checkInData.guestId !== 'new') {
      const existingOpen = await findOpenCheckInForGuest(checkInData.guestId);
      if (existingOpen) {
        throw new AlreadyCheckedInError(
          `${checkInData.guestFirstName} ${checkInData.guestLastName} is already checked in.`,
          existingOpen.id
        );
      }
    }

    // If it's a new guest, create a participant record first
    if (checkInData.guestId === 'new' && checkInData.newGuestDetails) {
      // Prepare image data if photo is provided
      let imageBase64 = null;
      let imageContentType = 'image/jpeg';

      if (checkInData.newGuestPhoto) {
        try {
          // Convert File to base64 for V2 API
          const reader = new FileReader();
          const photoData = await new Promise((resolve, reject) => {
            reader.onload = () => {
              const base64 = reader.result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(checkInData.newGuestPhoto);
          });
          imageBase64 = photoData;
          imageContentType = checkInData.newGuestPhoto.type || 'image/jpeg';
        } catch (imgError) {
          console.error('Error preparing image for V2 API:', imgError);
        }
      }

      // Try V2 API first if image is provided (for direct CCB image upload)
      if (imageBase64) {
        try {
          const { createCCBProfileV2 } = await import('./functionsService');
          const ccbResult = await createCCBProfileV2({
            profileData: {
              firstName: checkInData.guestFirstName,
              lastName: checkInData.guestLastName,
              gender: checkInData.newGuestDetails.gender,
              birthday: checkInData.newGuestDetails.birthday,
              email: checkInData.newGuestDetails.email,
              contactPhone: checkInData.newGuestDetails.mobilePhone || checkInData.newGuestDetails.homePhone,
              mobilePhone: checkInData.newGuestDetails.mobilePhone,
              homePhone: checkInData.newGuestDetails.homePhone,
              street: checkInData.newGuestDetails.street,
              city: checkInData.newGuestDetails.city,
              state: checkInData.newGuestDetails.state,
              zip: checkInData.newGuestDetails.zip,
              emergencyContactName: checkInData.newGuestDetails.emergencyContactName,
              emergencyContactPhone: checkInData.newGuestDetails.emergencyContactPhone
            },
            imageBase64: imageBase64,
            imageContentType: imageContentType
          });

          ccbId = ccbResult.ccbIdWithPrefix; // Use ccb_123 format
          ccbImageUploaded = ccbResult.imageUploaded;
          addedToCCBGroup = ccbResult.addedToGroup === true;
          console.log(`Created CCB profile (V2) with ID: ${ccbId}, image uploaded to CCB: ${ccbImageUploaded}, addedToGroup: ${addedToCCBGroup}`);
        } catch (v2Error) {
          console.error('Error creating CCB profile with V2 API:', v2Error);
          // Fall back to V1 API
        }
      }

      // Fall back to V1 API if V2 failed or no image provided
      if (!ccbId) {
        try {
          const { createCCBProfile } = await import('./functionsService');
          const ccbResult = await createCCBProfile({
            profileData: {
              first_name: checkInData.guestFirstName,
              last_name: checkInData.guestLastName,
              gender: checkInData.newGuestDetails.gender,
              birthday: checkInData.newGuestDetails.birthday,
              email: checkInData.newGuestDetails.email,
              contact_phone: checkInData.newGuestDetails.mobilePhone || checkInData.newGuestDetails.homePhone,
              mobile_phone: checkInData.newGuestDetails.mobilePhone,
              home_phone: checkInData.newGuestDetails.homePhone,
              mailing_street_address: checkInData.newGuestDetails.street,
              mailing_city: checkInData.newGuestDetails.city,
              mailing_state: checkInData.newGuestDetails.state,
              mailing_zip: checkInData.newGuestDetails.zip,
              emergency_contact_name: checkInData.newGuestDetails.emergencyContactName,
              emergency_phone: checkInData.newGuestDetails.emergencyContactPhone
            }
          });

          ccbId = ccbResult.ccbIdWithPrefix; // Use ccb_123 format
          addedToCCBGroup = ccbResult.addedToGroup === true;
          console.log(`Created CCB profile (V1) with ID: ${ccbId}, addedToGroup: ${addedToCCBGroup}`);
        } catch (ccbError) {
          console.error('Error creating CCB profile:', ccbError);
          // Continue without CCB ID - will use Firestore-generated ID
        }
      }

      // Use CCB ID if available, otherwise generate Firestore ID
      const participantsRef = collection(db, "participants");
      const newParticipantRef = ccbId ? doc(participantsRef, ccbId) : doc(participantsRef);
      guestId = newParticipantRef.id;

      const participantData = {
        firstName: checkInData.guestFirstName,
        lastName: checkInData.guestLastName,
        gender: checkInData.newGuestDetails.gender || '',
        birthday: checkInData.newGuestDetails.birthday || '',
        phone: checkInData.newGuestDetails.mobilePhone || checkInData.newGuestDetails.homePhone || '',
        homePhone: checkInData.newGuestDetails.homePhone || '',
        mobilePhone: checkInData.newGuestDetails.mobilePhone || '',
        email: checkInData.newGuestDetails.email || '',
        street: checkInData.newGuestDetails.street || '',
        city: checkInData.newGuestDetails.city || '',
        state: checkInData.newGuestDetails.state || '',
        zip: checkInData.newGuestDetails.zip || '',
        emergencyContactName: checkInData.newGuestDetails.emergencyContactName || '',
        emergencyContactPhone: checkInData.newGuestDetails.emergencyContactPhone || '',
        createdAt: serverTimestamp(),
        source: ccbId ? 'ccb' : 'app', // Mark as CCB if we got a CCB ID
        groupId: '1269',
        // Flag participants whose initial group-add failed so the scheduled
        // reconciler picks them up. Only meaningful for ccb-linked guests.
        ...(ccbId && !addedToCCBGroup ? { needsGroupAdd: true } : {}),
        // Flag stranded app-only participants (CCB profile create failed)
        // so scheduledCCBProfileCreateReconcile picks them up and retries.
        ...(!ccbId ? { needsCCBProfileCreate: true } : {})
      };

      // Create the participant document FIRST
      await setDoc(newParticipantRef, participantData);

      // Upload photo to Firebase Storage (always do this for local app access)
      // Even if uploaded to CCB, we want a copy in Firebase Storage
      if (checkInData.newGuestPhoto) {
        const { uploadGuestPhoto } = await import('./firestoreService');
        try {
          const photoURL = await uploadGuestPhoto(guestId, checkInData.newGuestPhoto);
          // Update the document with photo URL
          await setDoc(newParticipantRef, {
            image: photoURL,
            imageSource: 'app',
            ccbImageUploaded: ccbImageUploaded // Track if also uploaded to CCB
          }, { merge: true });
        } catch (photoError) {
          console.error('Error uploading new guest photo to Firebase:', photoError);
          // Continue without photo if upload fails
        }
      }
    }

    const checkInsRef = collection(db, "checkIns");
    const checkInId = doc(checkInsRef).id;

    // Build check-in notes - append photo reminder for new guests without a photo
    let checkInNotes = checkInData.checkInNotes || "";
    if (checkInData.guestId === 'new' && !checkInData.newGuestPhoto) {
      const photoReminder = `Photo needed - please capture image in CCB for ${checkInData.guestFirstName} ${checkInData.guestLastName}`;
      checkInNotes = checkInNotes ? `${checkInNotes} | ${photoReminder}` : photoReminder;
    }

    const checkInDoc = {
      guestId: guestId,
      guestFirstName: checkInData.guestFirstName,
      guestLastName: checkInData.guestLastName,
      serviceType: checkInData.serviceType, // '9am', '12pm', '3pm', 'tuesday_prayer'
      checkInTime: serverTimestamp(),
      checkInBy: checkInData.checkInBy,
      checkInByName: checkInData.checkInByName,
      checkInNotes: checkInNotes,
      pagerNumber: checkInData.pagerNumber || "",
      expectedPickupTime: checkInData.expectedPickupTime || "",
      status: "checked_in",
      checkOutTime: null,
      checkOutBy: null,
      checkOutByName: null,
      checkOutNotes: null,
      pagerRetrieved: false,
      actualPickupTime: "",
      vidNumber: "",
      licPlateNumber: "",
      vehicleColor: "",
      pickupNotes: ""
    };

    // Atomic double check-in guard. `activeCheckIns/{guestId}` is a lock doc
    // that points at the guest's current open check-in. The transaction:
    //   1. reads the lock; if it points at a check-in that is still open,
    //      the guest is already checked in -> reject.
    //   2. otherwise writes the new check-in AND (re)points the lock at it,
    //      atomically. This closes the sub-second race the pre-check query
    //      above cannot (two devices submitting at the same instant).
    // The lock self-heals: a stale lock left behind after check-out points at
    // a now-checked_out record, so step 1 lets the next check-in through
    // without needing check-out to clean the lock up.
    const guardRef = doc(db, "activeCheckIns", guestId);
    const checkInRef = doc(db, "checkIns", checkInId);
    await runTransaction(db, async (tx) => {
      const guardSnap = await tx.get(guardRef);
      if (guardSnap.exists()) {
        const existingId = guardSnap.data().checkInId;
        if (existingId) {
          const existingSnap = await tx.get(doc(db, "checkIns", existingId));
          if (existingSnap.exists() && existingSnap.data().status === 'checked_in') {
            throw new AlreadyCheckedInError(
              `${checkInData.guestFirstName} ${checkInData.guestLastName} is already checked in.`,
              existingId
            );
          }
        }
      }
      tx.set(checkInRef, checkInDoc);
      tx.set(guardRef, {
        checkInId,
        guestId,
        guestName: `${checkInData.guestFirstName} ${checkInData.guestLastName}`,
        serviceType: checkInData.serviceType,
        checkInTime: serverTimestamp()
      });
    });

    // Ensure CCB-linked guests are in group 1269. For pre-existing CCB
    // guests we still fire an immediate idempotent add (cheap and ensures
    // any older profiles missing from the group get added on next visit).
    // For brand-new profiles whose initial server-side add already failed,
    // the scheduled reconciler will pick them up via `needsGroupAdd`.
    if (guestId && guestId.startsWith('ccb_') && addedToCCBGroup) {
      import('./functionsService').then(({ addToCCBGroup }) => {
        addToCCBGroup({ individualId: guestId }).catch(err =>
          console.error('Background add-to-group failed:', err)
        );
      });
    }

    if (guestId && guestId.startsWith('ccb_')) {

      // Record guest attendance in CCB event (background, with result tracking)
      import('./functionsService').then(({ recordCCBEventAttendance }) => {
        recordCCBEventAttendance({
          serviceType: checkInData.serviceType,
          checkInType: 'guest',
          individualIds: [guestId],
          notes: checkInData.checkInNotes || ''
        }).then(result => {
          updateDoc(doc(db, "checkIns", checkInId), {
            ccbAttendanceResult: result
          }).catch(() => {});
        }).catch(err => {
          console.error('Background CCB guest attendance recording failed:', err);
          updateDoc(doc(db, "checkIns", checkInId), {
            ccbAttendanceResult: { success: false, error: err.message || String(err) }
          }).catch(() => {});
        });
      });
    }

    return checkInId;
  } catch (error) {
    console.error("Error checking in guest:", error);
    throw error;
  }
};

/**
 * Add a note to a guest profile (without creating a check-in)
 * @param {Object} noteData - Note data
 * @param {string} noteData.guestId - Guest participant ID
 * @param {string} noteData.guestFirstName - Guest first name
 * @param {string} noteData.guestLastName - Guest last name
 * @param {string} noteData.noteText - The note content
 * @param {string} noteData.createdBy - User UID who created the note
 * @param {string} noteData.createdByName - User display name
 * @returns {Promise<string>} The created note ID
 */
export const addGuestNote = async (noteData) => {
  try {
    const checkInsRef = collection(db, "checkIns");
    const noteId = doc(checkInsRef).id;

    const noteDoc = {
      guestId: noteData.guestId,
      guestFirstName: noteData.guestFirstName,
      guestLastName: noteData.guestLastName,
      serviceType: 'note', // Mark as 'note' not 'profile_note' or service time
      checkInTime: serverTimestamp(),
      checkInBy: noteData.createdBy,
      checkInByName: noteData.createdByName,
      checkInNotes: noteData.noteText,
      pagerNumber: "",
      status: null, // No status - this is just a note, not a check-in
      checkOutTime: null,
      checkOutBy: null,
      checkOutByName: null,
      checkOutNotes: null,
      pagerRetrieved: false
    };

    await setDoc(doc(db, "checkIns", noteId), noteDoc);
    console.log(`Created note ${noteId} for guest ${noteData.guestId}`);
    return noteId;
  } catch (error) {
    console.error("Error adding guest note:", error);
    throw error;
  }
};

/**
 * Check out a guest
 * @param {string} checkInId
 * @param {Object} checkOutData
 * @returns {Promise<void>}
 */
export const checkOutGuest = async (checkInId, checkOutData) => {
  try {
    const checkInRef = doc(db, "checkIns", checkInId);

    // Get the check-in record to check for pager number
    const checkInSnap = await getDoc(checkInRef);
    const checkInRecord = checkInSnap.data();

    await updateDoc(checkInRef, {
      checkOutTime: serverTimestamp(),
      checkOutBy: checkOutData.checkOutBy,
      checkOutByName: checkOutData.checkOutByName,
      checkOutNotes: checkOutData.checkOutNotes || "",
      pagerRetrieved: checkOutData.pagerRetrieved || false,
      actualPickupTime: checkOutData.actualPickupTime || "",
      vidNumber: checkOutData.vidNumber || "",
      licPlateNumber: checkOutData.licPlateNumber || "",
      vehicleColor: checkOutData.vehicleColor || "",
      pickupNotes: (checkOutData.pickupNotes || "").slice(0, 140),
      status: "checked_out"
    });

    // Auto-mark pager as missing if not retrieved
    if (checkInRecord?.pagerNumber && !checkOutData.pagerRetrieved) {
      const pagerNum = checkInRecord.pagerNumber;
      const pagerRef = doc(db, "pagerInventory", pagerNum);
      await setDoc(pagerRef, {
        pagerNumber: pagerNum,
        status: "missing",
        markedMissingAt: serverTimestamp(),
        markedMissingBy: checkOutData.checkOutBy,
        markedMissingByName: checkOutData.checkOutByName,
        guestName: `${checkInRecord.guestFirstName} ${checkInRecord.guestLastName}`,
        checkInId: checkInId,
        notes: "Auto-marked missing: pager not returned at checkout"
      });

      // Log the missing action
      const logRef = doc(collection(db, "pagerRecoveryLog"));
      await setDoc(logRef, {
        pagerNumber: pagerNum,
        action: "marked_missing",
        timestamp: serverTimestamp(),
        performedBy: checkOutData.checkOutBy,
        performedByName: checkOutData.checkOutByName,
        guestName: `${checkInRecord.guestFirstName} ${checkInRecord.guestLastName}`,
        notes: "Auto-marked missing: pager not returned at checkout"
      });
    }
  } catch (error) {
    console.error("Error checking out guest:", error);
    throw error;
  }
};

/**
 * Delete a check-in record. Restricted by Firestore rules to sys_admin.
 * Intended for clearing test records — does not touch pager inventory.
 * @param {string} checkInId
 * @returns {Promise<void>}
 */
export const deleteCheckIn = async (checkInId) => {
  try {
    const checkInRef = doc(db, "checkIns", checkInId);
    await deleteDoc(checkInRef);
  } catch (error) {
    console.error("Error deleting check-in:", error);
    throw error;
  }
};

/**
 * Get currently checked-in guests
 * @returns {Promise<Array>} Array of checked-in guests
 */
export const fetchCurrentlyCheckedIn = async () => {
  try {
    const checkInsRef = collection(db, "checkIns");
    const q = query(
      checkInsRef,
      where("status", "==", "checked_in"),
      orderBy("checkInTime", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching currently checked-in guests:", error);
    throw error;
  }
};

// ==================== PARTICIPANTS (CACHED CCB DATA) ====================

/**
 * Subscribe to participants (cached from CCB with images)
 * @param {string} groupId - Group ID (defaults to '1269')
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToParticipants = (groupId = '1269', callback) => {
  const participantsRef = collection(db, "participants");
  const q = query(
    participantsRef,
    where("groupId", "==", groupId),
    orderBy("lastName")
  );

  return onSnapshot(q, (querySnapshot) => {
    const participants = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(participants);
  });
};

/**
 * Get all participants from Firestore cache
 * @param {string} groupId - Group ID (defaults to '1269')
 * @param {Object} options
 * @param {boolean} options.includeInactive - If true, include guests marked inactive in CCB (admin views only)
 * @returns {Promise<Array>} Array of participant objects
 */
export const fetchParticipants = async (groupId = '1269', { includeInactive = false } = {}) => {
  try {
    const participantsRef = collection(db, "participants");
    // Dual-query: prefer the multi-group `groupIds` array (so cross-group
    // members show up in both rosters), fall back to legacy `groupId` for
    // any docs that haven't been re-synced under the multi-group schema
    // yet. Merge by document ID. Sort client-side because the
    // array-contains + orderBy combo requires a composite index.
    const [arraySnap, legacySnap] = await Promise.all([
      getDocs(query(participantsRef, where("groupIds", "array-contains", groupId))),
      getDocs(query(participantsRef, where("groupId", "==", groupId)))
    ]);
    const map = new Map();
    [...arraySnap.docs, ...legacySnap.docs].forEach(d => {
      if (!map.has(d.id)) map.set(d.id, { id: d.id, ...d.data() });
    });
    const all = Array.from(map.values()).sort((a, b) =>
      (a.lastName || '').localeCompare(b.lastName || '')
    );
    return includeInactive ? all : all.filter(p => p.inactive !== true);
  } catch (error) {
    console.error("Error fetching participants:", error);
    throw error;
  }
};

/**
 * Update a participant in Firestore
 * @param {string} participantId - The participant ID
 * @param {Object} data - The data to update
 * @returns {Promise<void>}
 */
export const updateParticipant = async (participantId, data) => {
  try {
    const participantRef = doc(db, "participants", participantId);
    await updateDoc(participantRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error updating participant:", error);
    throw error;
  }
};

// ==================== REPORTING ====================

/**
 * Get check-ins within a date range
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Promise<Array>} Array of check-in records
 */
export const fetchCheckInsInRange = async (startDate, endDate) => {
  try {
    const checkInsRef = collection(db, "checkIns");
    const q = query(
      checkInsRef,
      where("checkInTime", ">=", Timestamp.fromDate(startDate)),
      where("checkInTime", "<=", Timestamp.fromDate(endDate)),
      orderBy("checkInTime", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching check-ins in range:", error);
    throw error;
  }
};

// ==================== PHOTO MANAGEMENT ====================

/**
 * Upload guest photo to Firebase Storage via Cloud Function
 * @param {string} participantId - Participant ID
 * @param {File} photoFile - Photo file to upload
 * @returns {Promise<string>} Photo URL
 */
export const uploadGuestPhoto = async (participantId, photoFile) => {
  try {
    const { functions } = await import('./config');

    // Convert file to base64
    const reader = new FileReader();
    const photoData = await new Promise((resolve, reject) => {
      reader.onload = () => {
        // Remove the data:image/jpeg;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(photoFile);
    });

    // Call the Cloud Function
    const uploadPhoto = httpsCallable(functions, 'uploadParticipantPhoto');
    const result = await uploadPhoto({
      participantId,
      photoData,
      contentType: photoFile.type || 'image/jpeg'
    });

    if (!result.data.success) {
      throw new Error('Upload failed');
    }

    return result.data.photoURL;
  } catch (error) {
    console.error("Error uploading guest photo:", error);
    throw error;
  }
};

/**
 * Remove guest photo from Firebase Storage via Cloud Function
 * @param {string} participantId - Participant ID
 * @returns {Promise<void>}
 */
export const removeGuestPhoto = async (participantId) => {
  try {
    const { functions } = await import('./config');

    // Call the Cloud Function
    const removePhoto = httpsCallable(functions, 'removeParticipantPhoto');
    const result = await removePhoto({ participantId });

    if (!result.data.success) {
      throw new Error('Photo removal failed');
    }
  } catch (error) {
    console.error("Error removing guest photo:", error);
    throw error;
  }
};

// ==================== USER MANAGEMENT ====================

/**
 * Get all users
 * @returns {Promise<Array>} Array of user objects
 */
export const fetchUsers = async () => {
  try {
    const usersRef = collection(db, "users");
    const querySnapshot = await getDocs(usersRef);

    const users = await Promise.all(
      querySnapshot.docs.map(async (userDoc) => {
        const roleDoc = await getDoc(doc(db, "userRoles", userDoc.id));
        return {
          id: userDoc.id,
          ...userDoc.data(),
          role: roleDoc.exists() ? roleDoc.data().role : "volunteer"
        };
      })
    );

    return users;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

/**
 * Update user role
 * @param {string} userId
 * @param {string} role
 * @param {string} assignedBy
 * @returns {Promise<void>}
 */
export const updateUserRole = async (userId, role, assignedBy) => {
  try {
    const roleRef = doc(db, "userRoles", userId);
    await setDoc(roleRef, {
      role: role,
      assignedAt: serverTimestamp(),
      assignedBy: assignedBy
    }, { merge: true });
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
};


// ==================== NOTE TRACKING ====================

/**
 * Subscribe to check-ins with notes in real-time
 * @param {string} userId - Current user's ID for read status tracking
 * @param {function} callback - Callback function to handle check-ins data
 * @returns {function} Unsubscribe function
 */
export const subscribeToCheckInsWithNotes = (userId, callback) => {
  try {
    let checkInNotesData = [];
    let checkOutNotesData = [];
    let readCheckInIds = new Set();
    let unsubscribeCheckInNotes = null;
    let unsubscribeCheckOutNotes = null;
    let unsubscribeNoteReads = null;

    // Merge both subscriptions, deduplicate, and trigger callback
    const updateCallback = () => {
      const docsMap = new Map();
      checkInNotesData.forEach(d => docsMap.set(d.id, d));
      checkOutNotesData.forEach(d => {
        if (!docsMap.has(d.id)) docsMap.set(d.id, d);
      });
      const merged = Array.from(docsMap.values()).map(checkIn => ({
        ...checkIn,
        isRead: readCheckInIds.has(checkIn.id)
      }));
      console.log(`subscribeToCheckInsWithNotes: ${merged.length} check-ins with notes, ${readCheckInIds.size} reads`);
      callback(merged);
    };

    const checkInsRef = collection(db, "checkIns");

    // Subscribe to check-ins with checkInNotes
    const q1 = query(
      checkInsRef,
      where("checkInNotes", "!=", ""),
      orderBy("checkInNotes"),
      orderBy("checkInTime", "desc")
    );
    unsubscribeCheckInNotes = onSnapshot(q1, (querySnapshot) => {
      checkInNotesData = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      updateCallback();
    });

    // Subscribe to check-ins with checkOutNotes
    const q2 = query(
      checkInsRef,
      where("checkOutNotes", "!=", ""),
      orderBy("checkOutNotes"),
      orderBy("checkInTime", "desc")
    );
    unsubscribeCheckOutNotes = onSnapshot(q2, (querySnapshot) => {
      checkOutNotesData = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      updateCallback();
    });

    // Subscribe to note reads for this user
    const noteReadsRef = collection(db, "noteReads");
    const readsQuery = query(
      noteReadsRef,
      where("userId", "==", userId)
    );
    unsubscribeNoteReads = onSnapshot(readsQuery, (querySnapshot) => {
      readCheckInIds = new Set();
      querySnapshot.docs.forEach(d => {
        readCheckInIds.add(d.data().checkInId);
      });
      updateCallback();
    });

    return () => {
      if (unsubscribeCheckInNotes) unsubscribeCheckInNotes();
      if (unsubscribeCheckOutNotes) unsubscribeCheckOutNotes();
      if (unsubscribeNoteReads) unsubscribeNoteReads();
    };
  } catch (error) {
    console.error("Error subscribing to check-ins with notes:", error);
    throw error;
  }
};

/**
 * Get count of unread notes for a user
 * @param {string} userId - User's ID
 * @returns {Promise<number>} Count of unread notes
 */
export const getUnreadNotesCount = async (userId) => {
  try {
    const checkInsRef = collection(db, "checkIns");

    // Query both checkInNotes and checkOutNotes
    const q1 = query(checkInsRef, where("checkInNotes", "!=", ""));
    const q2 = query(checkInsRef, where("checkOutNotes", "!=", ""));
    const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);

    // Merge and deduplicate IDs
    const checkInIds = new Set();
    snapshot1.docs.forEach(d => checkInIds.add(d.id));
    snapshot2.docs.forEach(d => checkInIds.add(d.id));

    if (checkInIds.size === 0) {
      return 0;
    }

    // Get read status for this user
    const noteReadsRef = collection(db, "noteReads");
    const readsQuery = query(
      noteReadsRef,
      where("userId", "==", userId)
    );
    const readsSnapshot = await getDocs(readsQuery);
    const readCheckInIds = new Set(
      readsSnapshot.docs.map(d => d.data().checkInId)
    );

    // Count unread notes
    const unreadCount = Array.from(checkInIds).filter(id => !readCheckInIds.has(id)).length;
    return unreadCount;
  } catch (error) {
    console.error("Error getting unread notes count:", error);
    throw error;
  }
};

/**
 * Get all check-ins with notes for a specific guest
 * @param {string} guestId - Guest's participant ID
 * @param {string} userId - Current user's ID for read status
 * @returns {Promise<Array>} Array of check-ins with notes and read status
 */
export const getGuestCheckInHistory = async (guestId, userId) => {
  try {
    const checkInsRef = collection(db, "checkIns");

    // Fetch all check-ins for this guest, filter for notes client-side
    const q = query(
      checkInsRef,
      where("guestId", "==", guestId)
    );

    const querySnapshot = await getDocs(q);
    const checkIns = querySnapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(c => (c.checkInNotes && c.checkInNotes.trim()) || (c.checkOutNotes && c.checkOutNotes.trim()));

    if (checkIns.length === 0) {
      return [];
    }

    // Get read status for this user
    const noteReadsRef = collection(db, "noteReads");
    const readsQuery = query(
      noteReadsRef,
      where("userId", "==", userId),
      where("guestId", "==", guestId)
    );
    const readsSnapshot = await getDocs(readsQuery);
    const readCheckInIds = new Set(
      readsSnapshot.docs.map(d => d.data().checkInId)
    );

    // Flatten into individual note entries with source labels
    const noteEntries = [];
    for (const checkIn of checkIns) {
      if (checkIn.checkInNotes && checkIn.checkInNotes.trim()) {
        const isProfileNote = checkIn.serviceType === 'note';
        noteEntries.push({
          id: checkIn.id + (checkIn.checkOutNotes ? '_checkin' : ''),
          checkInId: checkIn.id,
          noteText: checkIn.checkInNotes,
          source: isProfileNote ? 'Profile Note' : 'Check-In Note',
          serviceType: checkIn.serviceType,
          authorName: checkIn.checkInByName,
          timestamp: checkIn.checkInTime,
          isRead: readCheckInIds.has(checkIn.id),
          checkInByName: checkIn.checkInByName,
          checkInNotes: checkIn.checkInNotes,
          checkInTime: checkIn.checkInTime,
        });
      }
      if (checkIn.checkOutNotes && checkIn.checkOutNotes.trim()) {
        noteEntries.push({
          id: checkIn.id + '_checkout',
          checkInId: checkIn.id,
          noteText: checkIn.checkOutNotes,
          source: 'Check-Out Note',
          serviceType: checkIn.serviceType,
          authorName: checkIn.checkOutByName,
          timestamp: checkIn.checkOutTime,
          isRead: readCheckInIds.has(checkIn.id),
          checkInByName: checkIn.checkOutByName,
          checkInNotes: checkIn.checkOutNotes,
          checkInTime: checkIn.checkOutTime,
        });
      }
    }

    // Sort by timestamp descending
    noteEntries.sort((a, b) => {
      const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
      const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
      return timeB - timeA;
    });

    return noteEntries;
  } catch (error) {
    console.error("Error getting guest check-in history:", error);
    throw error;
  }
};

/**
 * Mark a note as read by a user
 * @param {string} userId - User's ID
 * @param {string} checkInId - Check-in document ID
 * @param {string} guestId - Guest's participant ID
 * @param {string} userName - User's display name
 * @returns {Promise<void>}
 */
export const markNoteAsRead = async (userId, checkInId, guestId, userName) => {
  try {
    const noteReadId = `${userId}_${checkInId}`;
    const noteReadRef = doc(db, "noteReads", noteReadId);

    // Check if already marked as read
    const existingRead = await getDoc(noteReadRef);
    if (existingRead.exists()) {
      console.log(`Note ${checkInId} already marked as read`);
      return; // Already read
    }

    console.log(`Marking note as read: ${noteReadId}`, { userId, checkInId, guestId });
    await setDoc(noteReadRef, {
      userId,
      checkInId,
      guestId,
      readAt: serverTimestamp(),
      readBy: userName
    });
    console.log(`Successfully marked note ${checkInId} as read`);
  } catch (error) {
    console.error("Error marking note as read:", error);
    throw error;
  }
};

/**
 * Mark all notes for a guest as read by a user
 * @param {string} userId - User's ID
 * @param {string} guestId - Guest's participant ID
 * @param {string} userName - User's display name
 * @returns {Promise<void>}
 */
export const markAllGuestNotesAsRead = async (userId, guestId, userName) => {
  try {
    // Get all check-ins with notes for this guest
    const checkIns = await getGuestCheckInHistory(guestId, userId);

    // Mark each unread note as read
    const markReadPromises = checkIns
      .filter(checkIn => !checkIn.isRead)
      .map(checkIn => markNoteAsRead(userId, checkIn.id, guestId, userName));

    await Promise.all(markReadPromises);
  } catch (error) {
    console.error("Error marking all guest notes as read:", error);
    throw error;
  }
};

/**
 * Get unread notes count for a specific guest (for a user)
 * @param {string} guestId - Guest's participant ID
 * @param {string} userId - User's ID
 * @returns {Promise<number>} Count of unread notes for this guest
 */
export const getGuestUnreadNotesCount = async (guestId, userId) => {
  try {
    const checkIns = await getGuestCheckInHistory(guestId, userId);
    return checkIns.filter(checkIn => !checkIn.isRead).length;
  } catch (error) {
    console.error("Error getting guest unread notes count:", error);
    throw error;
  }
};

/**
 * Get unread notes counts for ALL guests efficiently (only 2 queries instead of N*2)
 * @param {string} userId - User's ID
 * @returns {Promise<Object>} Object mapping guestId -> unread count
 */
export const getAllGuestsUnreadCounts = async (userId) => {
  try {
    const checkInsRef = collection(db, "checkIns");

    // Query check-ins with checkInNotes and checkOutNotes
    const q1 = query(
      checkInsRef,
      where("checkInNotes", "!=", ""),
      orderBy("checkInNotes"),
      orderBy("checkInTime", "desc")
    );
    const q2 = query(
      checkInsRef,
      where("checkOutNotes", "!=", ""),
      orderBy("checkOutNotes"),
      orderBy("checkInTime", "desc")
    );

    // Get note reads for this user
    const noteReadsRef = collection(db, "noteReads");
    const readsQuery = query(
      noteReadsRef,
      where("userId", "==", userId)
    );

    const [snapshot1, snapshot2, readsSnapshot] = await Promise.all([
      getDocs(q1), getDocs(q2), getDocs(readsQuery)
    ]);

    // Merge and deduplicate check-in docs
    const docsMap = new Map();
    snapshot1.docs.forEach(d => docsMap.set(d.id, d.data()));
    snapshot2.docs.forEach(d => {
      if (!docsMap.has(d.id)) docsMap.set(d.id, d.data());
    });

    // Build set of read check-in IDs
    const readCheckInIds = new Set();
    readsSnapshot.docs.forEach(d => {
      readCheckInIds.add(d.data().checkInId);
    });

    // Count unread notes per guest
    const guestsWithUnread = new Map();
    docsMap.forEach((data, checkInId) => {
      if (!readCheckInIds.has(checkInId)) {
        const guestId = data.guestId;
        guestsWithUnread.set(guestId, (guestsWithUnread.get(guestId) || 0) + 1);
      }
    });

    const result = Object.fromEntries(guestsWithUnread);
    return result;
  } catch (error) {
    console.error("Error getting all guests unread counts:", error);
    throw error;
  }
};

// ==================== SMS MESSAGES ====================

/**
 * Subscribe to SMS messages for a specific check-in
 * @param {string} checkInId - Check-in ID
 * @param {Function} callback - Callback with array of messages
 * @returns {Function} Unsubscribe function
 */
export const subscribeToSmsMessages = (checkInId, callback) => {
  const smsRef = collection(db, "smsMessages");
  const q = query(
    smsRef,
    where("checkInId", "==", checkInId),
    orderBy("sentAt", "asc")
  );

  return onSnapshot(q, (querySnapshot) => {
    const messages = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(messages);
  });
};

/**
 * Get SMS message history for a guest (across all check-ins)
 * @param {string} guestId - Guest participant ID
 * @returns {Promise<Array>} Array of SMS messages
 */
export const fetchGuestSmsHistory = async (guestId) => {
  try {
    const smsRef = collection(db, "smsMessages");
    const q = query(
      smsRef,
      where("guestId", "==", guestId),
      orderBy("sentAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching guest SMS history:", error);
    throw error;
  }
};

// ==================== PAGER INVENTORY ====================

/**
 * Fetch pager inventory settings (total pagers count)
 * @returns {Promise<Object>} Pager settings { totalPagers: number }
 */
export const fetchPagerSettings = async () => {
  try {
    const settingsRef = doc(db, "pagerSettings", "config");
    const settingsDoc = await getDoc(settingsRef);
    if (settingsDoc.exists()) {
      return settingsDoc.data();
    }
    return { totalPagers: 0 };
  } catch (error) {
    console.error("Error fetching pager settings:", error);
    throw error;
  }
};

/**
 * Update pager inventory settings (total pagers count)
 * @param {number} totalPagers - Total number of pagers on hand
 * @returns {Promise<void>}
 */
export const updatePagerSettings = async (totalPagers) => {
  try {
    const settingsRef = doc(db, "pagerSettings", "config");
    await setDoc(settingsRef, {
      totalPagers: totalPagers,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error("Error updating pager settings:", error);
    throw error;
  }
};

/**
 * Fetch all pagers marked as missing from pagerInventory
 * @returns {Promise<Array>} Array of missing pager records
 */
export const fetchMissingPagers = async () => {
  try {
    const inventoryRef = collection(db, "pagerInventory");
    const q = query(
      inventoryRef,
      where("status", "==", "missing")
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));
  } catch (error) {
    console.error("Error fetching missing pagers:", error);
    throw error;
  }
};

/**
 * Fetch all pager inventory records
 * @returns {Promise<Array>} Array of all pager inventory records
 */
export const fetchAllPagerInventory = async () => {
  try {
    const inventoryRef = collection(db, "pagerInventory");
    const querySnapshot = await getDocs(inventoryRef);
    return querySnapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));
  } catch (error) {
    console.error("Error fetching pager inventory:", error);
    throw error;
  }
};

/**
 * Mark a pager as missing in the inventory
 * @param {string} pagerNumber - The pager number
 * @param {string} userId - UID of user marking it missing
 * @param {string} userName - Display name of user
 * @param {string} notes - Optional notes
 * @returns {Promise<void>}
 */
export const markPagerMissing = async (pagerNumber, userId, userName, notes = '') => {
  try {
    const pagerRef = doc(db, "pagerInventory", pagerNumber);
    await setDoc(pagerRef, {
      pagerNumber: pagerNumber,
      status: "missing",
      markedMissingAt: serverTimestamp(),
      markedMissingBy: userId,
      markedMissingByName: userName,
      notes: notes
    });

    // Log the action
    const logRef = doc(collection(db, "pagerRecoveryLog"));
    await setDoc(logRef, {
      pagerNumber: pagerNumber,
      action: "marked_missing",
      timestamp: serverTimestamp(),
      performedBy: userId,
      performedByName: userName,
      notes: notes
    });
  } catch (error) {
    console.error("Error marking pager as missing:", error);
    throw error;
  }
};

/**
 * Mark a pager as recovered/returned
 * @param {string} pagerNumber - The pager number
 * @param {string} userId - UID of user recording recovery
 * @param {string} userName - Display name of user
 * @param {string} returnedByName - Name of person who returned the pager
 * @param {string} notes - Optional notes
 * @returns {Promise<void>}
 */
export const markPagerRecovered = async (pagerNumber, userId, userName, returnedByName, notes = '') => {
  try {
    const pagerRef = doc(db, "pagerInventory", pagerNumber);
    await setDoc(pagerRef, {
      pagerNumber: pagerNumber,
      status: "recovered",
      recoveredAt: serverTimestamp(),
      recoveredBy: userId,
      recoveredByName: userName,
      returnedByName: returnedByName,
      recoveryNotes: notes
    }, { merge: true });

    // Log the recovery action
    const logRef = doc(collection(db, "pagerRecoveryLog"));
    await setDoc(logRef, {
      pagerNumber: pagerNumber,
      action: "recovered",
      timestamp: serverTimestamp(),
      performedBy: userId,
      performedByName: userName,
      returnedByName: returnedByName,
      notes: notes
    });

    // Remove from missing status (delete the inventory record since it's recovered)
    await deleteDoc(pagerRef);
  } catch (error) {
    console.error("Error marking pager as recovered:", error);
    throw error;
  }
};

/**
 * Fetch pager recovery log (audit trail)
 * @param {number} limitCount - Max records to fetch (default 50)
 * @returns {Promise<Array>} Array of recovery log entries
 */
export const fetchPagerRecoveryLog = async (limitCount = 50) => {
  try {
    const logRef = collection(db, "pagerRecoveryLog");
    const q = query(
      logRef,
      orderBy("timestamp", "desc")
    );
    const querySnapshot = await getDocs(q);
    const logs = querySnapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));
    return logs.slice(0, limitCount);
  } catch (error) {
    console.error("Error fetching pager recovery log:", error);
    throw error;
  }
};

/**
 * Check if a specific pager number is currently marked as missing
 * @param {string} pagerNumber - The pager number to check
 * @returns {Promise<boolean>} True if pager is missing
 */
export const isPagerMissing = async (pagerNumber) => {
  try {
    const pagerRef = doc(db, "pagerInventory", pagerNumber);
    const pagerDoc = await getDoc(pagerRef);
    return pagerDoc.exists() && pagerDoc.data().status === "missing";
  } catch (error) {
    console.error("Error checking pager status:", error);
    return false;
  }
};

/**
 * Mark a pager as returned from the unreturned pagers list.
 * Updates the checkIn record (pagerRetrieved = true) and handles pager inventory cleanup.
 * @param {string} checkInId - The check-in document ID
 * @param {string} pagerNumber - The pager number
 * @param {string} userId - UID of user recording the return
 * @param {string} userName - Display name of user
 * @param {string} returnedByName - Name of person who returned the pager
 * @param {string} notes - Optional notes
 * @returns {Promise<void>}
 */
export const markPagerReturnedFromCheckIn = async (checkInId, pagerNumber, userId, userName, returnedByName, notes = '') => {
  try {
    // Update the check-in record to mark pager as retrieved
    const checkInRef = doc(db, "checkIns", checkInId);
    await updateDoc(checkInRef, {
      pagerRetrieved: true
    });

    // Check if pager is in the missing inventory and clean it up
    const pagerRef = doc(db, "pagerInventory", pagerNumber);
    const pagerDoc = await getDoc(pagerRef);
    if (pagerDoc.exists() && pagerDoc.data().status === "missing") {
      // Log recovery before deleting
      const logRef = doc(collection(db, "pagerRecoveryLog"));
      await setDoc(logRef, {
        pagerNumber: pagerNumber,
        action: "recovered",
        timestamp: serverTimestamp(),
        performedBy: userId,
        performedByName: userName,
        returnedByName: returnedByName,
        notes: notes
      });

      await deleteDoc(pagerRef);
    } else {
      // Still log the return even if not in inventory
      const logRef = doc(collection(db, "pagerRecoveryLog"));
      await setDoc(logRef, {
        pagerNumber: pagerNumber,
        action: "recovered",
        timestamp: serverTimestamp(),
        performedBy: userId,
        performedByName: userName,
        returnedByName: returnedByName,
        notes: notes
      });
    }
  } catch (error) {
    console.error("Error marking pager as returned:", error);
    throw error;
  }
};

/**
 * Fetch pager numbers currently assigned to checked-in guests
 * @returns {Promise<Set<string>>} Set of pager numbers currently in use
 */
export const fetchAssignedPagerNumbers = async () => {
  try {
    const checkInsRef = collection(db, "checkIns");
    const q = query(
      checkInsRef,
      where("status", "==", "checked_in")
    );
    const querySnapshot = await getDocs(q);
    const assigned = new Set();
    querySnapshot.docs.forEach(d => {
      const pNum = d.data().pagerNumber;
      if (pNum) {
        assigned.add(String(pNum));
      }
    });
    return assigned;
  } catch (error) {
    console.error("Error fetching assigned pager numbers:", error);
    return new Set();
  }
};

/**
 * Fetch pager numbers that were checked out but never physically returned
 * (status "checked_out" with pagerRetrieved !== true). These pagers are not in
 * hand, so they must not be offered for a new check-in — this mirrors the Pager
 * Report's "Not in Inventory" definition.
 * @returns {Promise<Set<string>>} Set of unreturned pager numbers
 */
export const fetchUnreturnedPagerNumbers = async () => {
  try {
    const checkInsRef = collection(db, "checkIns");
    const q = query(
      checkInsRef,
      where("status", "==", "checked_out")
    );
    const querySnapshot = await getDocs(q);
    const unreturned = new Set();
    querySnapshot.docs.forEach(d => {
      const data = d.data();
      if (data.pagerNumber && data.pagerRetrieved !== true && data.guestDeleted !== true) {
        unreturned.add(String(data.pagerNumber));
      }
    });
    return unreturned;
  } catch (error) {
    console.error("Error fetching unreturned pager numbers:", error);
    return new Set();
  }
};

// ==================== VOLUNTEER CHECK-IN ====================

// Firestore error codes worth retrying: they signal a transport or contention
// problem, not a rejected write. `unavailable` and `deadline-exceeded` in
// particular are what a flaky connection produces, and retrying them is the
// difference between a check-in that lands and one the operator has to redo.
const RETRYABLE_FIRESTORE_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'aborted',
  'internal',
  'cancelled',
  'resource-exhausted',
]);

const isRetryable = (err) =>
  RETRYABLE_FIRESTORE_CODES.has(err?.code) ||
  // Offline errors surface as a generic failure with this message shape.
  /network|offline|transport|timeout/i.test(err?.message || '');

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Check in one or more volunteers
 * Creates records in the volunteerCheckIns collection and records CCB attendance
 *
 * Per-volunteer outcomes are collected rather than thrown. A single volunteer
 * failing used to abort the whole loop and reject, discarding the fact that
 * earlier volunteers had already been written — the caller reported a total
 * failure while the records existed in Firestore. Every volunteer now lands in
 * exactly one of `checkInIds`, `skipped`, or `failed`, and the caller decides
 * what to tell the operator.
 *
 * @param {Object} data
 * @param {Array<Object>} data.volunteers - Array of {id, firstName, lastName, notes} objects
 * @param {string} data.serviceType - Service type ID
 * @param {string} data.checkInBy - User UID
 * @param {string} data.checkInByName - User display name
 * @returns {Promise<Object>} { success, count, checkInIds, skipped, failed }
 */
export const checkInVolunteers = async (data) => {
  try {
    const { volunteers, serviceType, checkInBy, checkInByName } = data;

    if (!volunteers || volunteers.length === 0) {
      throw new Error('At least one volunteer is required');
    }
    if (!serviceType) {
      throw new Error('Service type is required');
    }

    const checkInIds = [];
    const ccbIndividualIds = [];
    // Volunteers rejected because they were already checked in for this
    // service today. Not an error: the desired end state already holds.
    const skipped = [];
    // Volunteers whose write genuinely did not land, after retries.
    const failed = [];
    const dateKey = getLocalDateKey();

    for (const volunteer of volunteers) {
      // Deterministic doc ID => a volunteer can only be checked in once per
      // service per calendar day. A concurrent duplicate submit resolves to the
      // same ID; the transaction below rejects the second writer atomically.
      // It also makes a retry safe: re-running this write cannot produce a
      // second record.
      const checkInId = `${volunteer.id}_${dateKey}_${serviceType}`;
      const checkInRef = doc(db, "volunteerCheckIns", checkInId);

      const checkInDoc = {
        volunteerId: volunteer.id,
        volunteerFirstName: volunteer.firstName,
        volunteerLastName: volunteer.lastName,
        serviceType: serviceType,
        checkInTime: serverTimestamp(),
        checkInBy: checkInBy,
        checkInByName: checkInByName,
        notes: volunteer.notes || ""
      };

      const name = `${volunteer.firstName} ${volunteer.lastName}`;
      let lastError = null;
      let outcome = null; // 'written' | 'exists'

      // Up to 3 attempts with backoff. Safe because the doc ID is
      // deterministic and the transaction re-reads before writing.
      for (let attempt = 0; attempt < 3 && !outcome; attempt++) {
        try {
          await runTransaction(db, async (tx) => {
            const existing = await tx.get(checkInRef);
            if (existing.exists()) {
              const err = new Error('ALREADY_CHECKED_IN');
              err.code = 'ALREADY_CHECKED_IN';
              throw err;
            }
            tx.set(checkInRef, checkInDoc);
          });
          outcome = 'written';
        } catch (err) {
          if (err.code === 'ALREADY_CHECKED_IN') {
            outcome = 'exists';
          } else if (isRetryable(err) && attempt < 2) {
            lastError = err;
            await delay(400 * (attempt + 1));
          } else {
            lastError = err;
            break;
          }
        }
      }

      if (outcome === 'written') {
        checkInIds.push(checkInId);
        // Collect CCB-linked volunteer IDs for attendance recording
        if (volunteer.id && volunteer.id.startsWith('ccb_')) {
          ccbIndividualIds.push(volunteer.id);
        }
      } else if (outcome === 'exists') {
        skipped.push({ id: volunteer.id, name });
      } else {
        // A commit whose acknowledgement was lost leaves the document in place
        // even though we saw an error. Read it back before calling this a
        // failure — otherwise we tell the operator to redo a check-in that
        // actually succeeded, which is exactly the confusion being fixed.
        let landedAnyway = false;
        try {
          const check = await getDoc(checkInRef);
          landedAnyway = check.exists();
        } catch {
          // Can't confirm either way; fall through and report the failure.
        }

        if (landedAnyway) {
          checkInIds.push(checkInId);
          if (volunteer.id && volunteer.id.startsWith('ccb_')) {
            ccbIndividualIds.push(volunteer.id);
          }
        } else {
          failed.push({
            id: volunteer.id,
            name,
            code: lastError?.code || 'unknown',
            message: lastError?.message || 'Check-in write failed',
          });
        }
      }
    }

    // Record volunteer attendance in CCB event (background, with result tracking)
    // Only record to CCB for scheduled services (not 'unscheduled' off-hours check-ins)
    if (ccbIndividualIds.length > 0 && serviceType !== 'unscheduled') {
      import('./functionsService').then(({ recordCCBEventAttendance }) => {
        recordCCBEventAttendance({
          serviceType: serviceType,
          checkInType: 'volunteer',
          individualIds: ccbIndividualIds
        }).then(result => {
          // Track result on each volunteer check-in doc
          checkInIds.forEach(id => {
            updateDoc(doc(db, "volunteerCheckIns", id), {
              ccbAttendanceResult: result
            }).catch(() => {});
          });
        }).catch(err => {
          console.error('Background CCB volunteer attendance recording failed:', err);
          checkInIds.forEach(id => {
            updateDoc(doc(db, "volunteerCheckIns", id), {
              ccbAttendanceResult: { success: false, error: err.message || String(err) }
            }).catch(() => {});
          });
        });
      });
    }

    return {
      // The operation as a whole succeeded only if nothing was left unwritten.
      success: failed.length === 0,
      count: checkInIds.length,
      checkInIds,
      skipped,
      failed
    };
  } catch (error) {
    console.error("Error checking in volunteers:", error);
    throw error;
  }
};

/**
 * Subscribe to volunteer check-ins for a given date (real-time).
 * Mirrors subscribeToTodayCheckIns but for the volunteerCheckIns collection.
 * @param {Function} callback - Called with array of volunteer check-in records
 * @param {Date} [selectedDate] - Defaults to today
 * @returns {Function} Unsubscribe function
 */
export const subscribeToVolunteerCheckInsForDate = (callback, selectedDate = null) => {
  const targetDate = selectedDate || new Date();
  const startOfDay = new Date(targetDate);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(targetDate);
  endOfDay.setHours(23, 59, 59, 999);

  const checkInsRef = collection(db, "volunteerCheckIns");
  const q = query(
    checkInsRef,
    where("checkInTime", ">=", Timestamp.fromDate(startOfDay)),
    where("checkInTime", "<=", Timestamp.fromDate(endOfDay)),
    orderBy("checkInTime", "desc")
  );

  return onSnapshot(q, (querySnapshot) => {
    const checkIns = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(checkIns);
  });
};

/**
 * Fetch today's volunteer check-ins
 * @returns {Promise<Array>} Array of today's volunteer check-in records
 */
export const fetchTodayVolunteerCheckIns = async () => {
  try {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const checkInsRef = collection(db, "volunteerCheckIns");
    const q = query(
      checkInsRef,
      where("checkInTime", ">=", Timestamp.fromDate(startOfDay)),
      where("checkInTime", "<=", Timestamp.fromDate(endOfDay)),
      orderBy("checkInTime", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));
  } catch (error) {
    console.error("Error fetching today's volunteer check-ins:", error);
    return [];
  }
};

/**
 * Fetch all volunteer check-ins for a specific volunteer
 * @param {string} volunteerId - Volunteer participant ID
 * @returns {Promise<Array>} Array of check-in records sorted by date descending
 */
export const fetchVolunteerCheckInHistory = async (volunteerId) => {
  try {
    const checkInsRef = collection(db, "volunteerCheckIns");
    const q = query(
      checkInsRef,
      where("volunteerId", "==", volunteerId),
      orderBy("checkInTime", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));
  } catch (error) {
    console.error("Error fetching volunteer check-in history:", error);
    return [];
  }
};

// ==================== VOLUNTEER NOTES ====================
//
// Volunteers have two kinds of notes:
//   - check-in notes: the `notes` field on a volunteerCheckIns doc
//   - profile notes:  docs in volunteerNotes, added from Volunteer Profiles
// Profile notes used to be written into the guest checkIns collection, where
// they never showed on the volunteer's profile and were counted as guest notes.
//
// Read tracking reuses noteReads. A volunteer read is keyed by
// `volunteerNoteKey` ("checkin:<id>" or "profile:<id>") so it never mixes with
// guest reads, which key on `checkInId`.

export const volunteerNoteKey = (kind, id) => `${kind}:${id}`;

/**
 * Add a profile note for a volunteer.
 * @param {Object} noteData { volunteerId, volunteerFirstName, volunteerLastName, noteText, createdBy, createdByName }
 * @returns {Promise<string>} New note ID
 */
export const addVolunteerNote = async (noteData) => {
  const ref = doc(collection(db, "volunteerNotes"));
  await setDoc(ref, {
    volunteerId: noteData.volunteerId,
    volunteerFirstName: noteData.volunteerFirstName || '',
    volunteerLastName: noteData.volunteerLastName || '',
    note: noteData.noteText,
    createdBy: noteData.createdBy,
    createdByName: noteData.createdByName,
    createdAt: serverTimestamp()
  });
  return ref.id;
};

/**
 * Fetch a volunteer's profile notes, newest first. Sorted client-side so the
 * query needs no composite index.
 * @param {string} volunteerId
 * @returns {Promise<Array>}
 */
export const fetchVolunteerNotes = async (volunteerId) => {
  try {
    const snap = await getDocs(query(collection(db, "volunteerNotes"), where("volunteerId", "==", volunteerId)));
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  } catch (error) {
    console.error("Error fetching volunteer notes:", error);
    return [];
  }
};

/**
 * Subscribe to a user's unread volunteer notes (check-in and profile notes).
 * @param {string} userId - User's ID
 * @param {Function} callback - Receives { [volunteerId]: unreadCount }
 * @returns {Function} Unsubscribe
 */
export const subscribeToUnreadVolunteerNotes = (userId, callback) => {
  let checkInNotes = [];
  let profileNotes = [];
  let readKeys = new Set();

  const emit = () => {
    const counts = {};
    [...checkInNotes, ...profileNotes].forEach(n => {
      // Your own notes are never unread to you
      if ((n.createdBy || n.checkInBy) === userId) return;
      if (!readKeys.has(n.key)) {
        counts[n.volunteerId] = (counts[n.volunteerId] || 0) + 1;
      }
    });
    callback(counts);
  };

  const unsubscribeCheckIns = onSnapshot(
    query(collection(db, "volunteerCheckIns"), where("notes", "!=", "")),
    (snapshot) => {
      checkInNotes = snapshot.docs
        .map(d => ({ key: volunteerNoteKey('checkin', d.id), ...d.data() }))
        .filter(n => n.volunteerId && n.notes && n.notes.trim());
      emit();
    },
    (err) => console.error("Error subscribing to volunteer check-in notes:", err)
  );

  const unsubscribeProfile = onSnapshot(
    collection(db, "volunteerNotes"),
    (snapshot) => {
      profileNotes = snapshot.docs
        .map(d => ({ key: volunteerNoteKey('profile', d.id), ...d.data() }))
        .filter(n => n.volunteerId);
      emit();
    },
    (err) => console.error("Error subscribing to volunteer profile notes:", err)
  );

  const unsubscribeReads = onSnapshot(
    query(collection(db, "noteReads"), where("userId", "==", userId)),
    (snapshot) => {
      readKeys = new Set();
      snapshot.docs.forEach(d => {
        const key = d.data().volunteerNoteKey;
        if (key) readKeys.add(key);
      });
      emit();
    },
    (err) => console.error("Error subscribing to volunteer note reads:", err)
  );

  return () => {
    unsubscribeCheckIns();
    unsubscribeProfile();
    unsubscribeReads();
  };
};

/**
 * Mark volunteer notes as read by a user. noteReads is create-only under the
 * security rules, so existing reads are skipped rather than rewritten.
 * @param {string} userId - User's ID
 * @param {string} volunteerId - Volunteer participant ID
 * @param {Array<string>} noteKeys - volunteerNoteKey() values
 * @param {string} userName - User's display name
 * @returns {Promise<void>}
 */
export const markVolunteerNotesAsRead = async (userId, volunteerId, noteKeys, userName) => {
  await Promise.all(noteKeys.map(async (key) => {
    const ref = doc(db, "noteReads", `${userId}_vol_${key.replace(':', '_')}`);
    const existing = await getDoc(ref);
    if (existing.exists()) return;
    await setDoc(ref, {
      userId,
      volunteerNoteKey: key,
      volunteerId,
      readAt: serverTimestamp(),
      readBy: userName
    });
  }));
};

// ==================== ADDITIONAL ATTENDANCE ====================

/**
 * Save additional attendance count for a specific date
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {number} sundayCount - Additional Sunday attendance count
 * @param {number} tuesdayCount - Additional Tuesday prayer meeting attendance count
 * @param {string} userId - UID of user saving
 * @param {string} userName - Display name of user saving
 */
export const saveAdditionalAttendance = async (dateStr, sundayCount, tuesdayCount, userId, userName) => {
  try {
    await setDoc(doc(db, "additionalAttendance", dateStr), {
      sundayCount: sundayCount || 0,
      tuesdayCount: tuesdayCount || 0,
      updatedBy: userId,
      updatedByName: userName,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error("Error saving additional attendance:", error);
    throw error;
  }
};

/**
 * Save granular additional attendance for a specific date.
 * Stores per-service per-location counts and also writes derived
 * sundayCount/tuesdayCount totals for backward compatibility with
 * existing report code.
 *
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {Object} counts - { [serviceId]: { [locationId]: number } }
 * @param {string} userId - UID of user saving
 * @param {string} userName - Display name of user saving
 */
export const saveAdditionalAttendanceCounts = async (dateStr, counts, userId, userName) => {
  try {
    const sumService = (serviceId) => {
      const buckets = counts?.[serviceId] || {};
      return Object.values(buckets).reduce((acc, n) => acc + (Number(n) || 0), 0);
    };
    const sundayCount = sumService('9am') + sumService('12pm') + sumService('3pm');
    const tuesdayCount = sumService('tuesday_prayer');

    await setDoc(doc(db, "additionalAttendance", dateStr), {
      counts: counts || {},
      sundayCount,
      tuesdayCount,
      updatedBy: userId,
      updatedByName: userName,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { sundayCount, tuesdayCount };
  } catch (error) {
    console.error("Error saving granular additional attendance:", error);
    throw error;
  }
};

/**
 * Fetch additional attendance for a specific date.
 * Always returns the granular `counts` shape — synthesizes one from legacy
 * sundayCount/tuesdayCount fields when the doc was written by an older
 * client (assigns the legacy total to 9am/163_livingston for Sundays and
 * tuesday_prayer/163_livingston for Tuesdays so it remains visible).
 *
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {Object} { counts, sundayCount, tuesdayCount }
 */
export const fetchAdditionalAttendance = async (dateStr) => {
  try {
    const docSnap = await getDoc(doc(db, "additionalAttendance", dateStr));
    if (!docSnap.exists()) {
      return { counts: {}, sundayCount: 0, tuesdayCount: 0 };
    }
    const data = docSnap.data();
    const sundayCount = data.sundayCount || 0;
    const tuesdayCount = data.tuesdayCount || 0;
    let counts = data.counts;
    if (!counts || typeof counts !== 'object') {
      counts = {};
      if (sundayCount > 0) {
        counts['9am'] = { '163_livingston': sundayCount };
      }
      if (tuesdayCount > 0) {
        counts['tuesday_prayer'] = { '163_livingston': tuesdayCount };
      }
    }
    return { counts, sundayCount, tuesdayCount };
  } catch (error) {
    console.error("Error fetching additional attendance:", error);
    return { counts: {}, sundayCount: 0, tuesdayCount: 0 };
  }
};

// ==================== INCIDENT NOTES ====================

/**
 * Add an incident/general note for a specific service time. Append-only —
 * never edited or deleted, so it serves as an immutable audit trail.
 * @param {Object} data
 * @param {string} data.serviceDate - YYYY-MM-DD
 * @param {string} data.serviceType - '9am' | '12pm' | '3pm' | 'tuesday_prayer' | 'general'
 * @param {string} data.noteText
 * @param {string} data.createdBy - User UID
 * @param {string} data.createdByName - Display name
 * @returns {Promise<string>} New note ID
 */
export const addIncidentNote = async ({ serviceDate, serviceType, noteText, createdBy, createdByName }) => {
  const notesRef = collection(db, "incidentNotes");
  const noteId = doc(notesRef).id;
  await setDoc(doc(db, "incidentNotes", noteId), {
    serviceDate,
    serviceType,
    noteText,
    createdBy,
    createdByName,
    createdAt: serverTimestamp()
  });
  return noteId;
};

/**
 * Subscribe to incident notes for a specific date. Sorted newest-first.
 * @param {string} dateStr - YYYY-MM-DD
 * @param {Function} callback - Receives Array of notes
 * @returns {Function} Unsubscribe
 */
export const subscribeToIncidentNotes = (dateStr, callback) => {
  const notesRef = collection(db, "incidentNotes");
  const q = query(notesRef, where("serviceDate", "==", dateStr));
  return onSnapshot(q, (snap) => {
    const notes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    notes.sort((a, b) => {
      const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return tb - ta;
    });
    callback(notes);
  });
};

/**
 * Fetch all incident notes whose serviceDate falls within a date range.
 * Sorted newest-first by createdAt.
 * @param {string} startDateStr - YYYY-MM-DD inclusive
 * @param {string} endDateStr - YYYY-MM-DD inclusive
 * @returns {Promise<Array>} Notes
 */
export const fetchIncidentNotesRange = async (startDateStr, endDateStr) => {
  const notesRef = collection(db, "incidentNotes");
  const q = query(
    notesRef,
    where("serviceDate", ">=", startDateStr),
    where("serviceDate", "<=", endDateStr)
  );
  const snap = await getDocs(q);
  const notes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  notes.sort((a, b) => {
    const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return tb - ta;
  });
  return notes;
};

/**
 * Fetch additional attendance for a date range
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Object} { totalSunday, totalTuesday }
 */
export const fetchAdditionalAttendanceRange = async (startDate, endDate) => {
  try {
    const startStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    const endStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

    const attendanceRef = collection(db, "additionalAttendance");
    const q = query(
      attendanceRef,
      where(documentId(), '>=', startStr),
      where(documentId(), '<=', endStr)
    );
    const snapshot = await getDocs(q);

    let totalSunday = 0;
    let totalTuesday = 0;
    snapshot.forEach(doc => {
      const data = doc.data();
      totalSunday += (data.sundayCount || 0);
      totalTuesday += (data.tuesdayCount || 0);
    });

    return { totalSunday, totalTuesday };
  } catch (error) {
    console.error("Error fetching additional attendance range:", error);
    return { totalSunday: 0, totalTuesday: 0 };
  }
};

// ==================== ACTIVITY & ERROR LOGS ====================

/**
 * Fetch client activity/error log entries for a date range.
 *
 * Deliberately queries on createdAt alone (a range plus an order on the same
 * field needs only the automatic single-field index) and leaves type/category/
 * user filtering to the caller. That keeps the admin viewer's filters free to
 * change without a new composite index and a deploy.
 *
 * @param {Object} options
 * @param {Date} options.startDate - inclusive lower bound
 * @param {Date} options.endDate - inclusive upper bound
 * @param {number} [options.limitCount=500] - max entries returned
 * @returns {Promise<Array>} entries, newest first
 */
export const fetchActivityLogs = async ({ startDate, endDate, limitCount = 500 }) => {
  try {
    const logsRef = collection(db, "activityLogs");
    const q = query(
      logsRef,
      where("createdAt", ">=", Timestamp.fromDate(startDate)),
      where("createdAt", "<=", Timestamp.fromDate(endDate)),
      orderBy("createdAt", "desc"),
      fsLimit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Error fetching activity logs:", error);
    throw error;
  }
};

/**
 * Fetch volunteer check-in records for a date range.
 * @param {Date} startDate - inclusive lower bound
 * @param {Date} endDate - inclusive upper bound
 * @returns {Promise<Array>} records, newest first
 */
export const fetchVolunteerCheckInsInRange = async (startDate, endDate) => {
  try {
    const checkInsRef = collection(db, "volunteerCheckIns");
    const q = query(
      checkInsRef,
      where("checkInTime", ">=", Timestamp.fromDate(startDate)),
      where("checkInTime", "<=", Timestamp.fromDate(endDate)),
      orderBy("checkInTime", "desc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Error fetching volunteer check-ins for range:", error);
    throw error;
  }
};
